// This reducer is for any state slices that are not lazily loaded.  Any lazy loaded
// state slices will extend this root state and add their own slice of state to it.
import { Action, ActionReducer, ActionReducerMap, MetaReducer } from '@ngrx/store';
//   import * as fromRouter from '@ngrx/router-store';
/**
 * storeFreeze prevents state from being mutated. When mutation occurs, an
 * exception will be thrown. This is useful during development mode to
 * ensure that none of the reducers accidentally mutates the state.
 */
import { storeFreeze } from 'ngrx-store-freeze';
import { localStorageSync } from 'ngrx-store-localstorage';
import { environment } from '../../environments/environment';
import * as fromAuthorization from '../auth';
import * as fromCompanySetting from '../company-setting';
import { LoginRequestStatus } from '../efaact-configuration/efaact-configuration.module';
import * as fromExpenses from '../expenses/store';
import * as fromLookups from '../lookups';
import * as fromOversign from '../oversign-time/store/oversign-time.reducer';
import * as fromReports from '../reporting';
import * as fromErrors from '../shared/errors-sidenav';
import * as fromLoading from '../shared/loading/store';
import * as fromProcesses from '../shared/process/store';
import { LoggingService } from '../shared/services/logging/logging.service';
import * as fromAuths from '../user-authorizations/store';
import * as fromUser from '../user-info';
import { userFeatureKey } from '../user-info';
import * as fromUserRole from '../user-role';
import * as fromVersion from './versionReducer';
import * as fromActions from './actions';
import * as fromQuickbooks from '../quickbooks/store';
import * as fromUserSetting from '../user-setting/store';
import * as fromDataServices from '../data-services';
/**
 * Every reducer module's default export is the reducer function itself. In
 * addition, each module should export a type or interface that describes
 * the state of the reducer plus any selector functions. The `* as`
 * notation packages up all of the exports into a single object.
 */
import * as fromCore from './core/core.reducer';

const log = new LoggingService();

/**
 * As mentioned, we treat each reducer like a table in a database. This means
 * our top level state interface is just a map of keys to inner state types.
 */
// TODO: everything other than layout is defined for testing right now,
// see how to remove that so all these aren't hard coded in here.
export interface State {
    core: fromCore.CoreState;
    loading: fromLoading.LoadingState;
    auth: fromAuthorization.State;
    [userFeatureKey]: fromUser.State;
    lookups: fromLookups.LookupState;
    reports: fromReports.State;
    user_role: fromUserRole.UserRoleState;
    errors: fromErrors.State;
    expenses: fromExpenses.State;
    companySettings: fromCompanySetting.State; // how to use fromCompanySetting.companySettingsFeatureKey?
    oversignTime: fromOversign.State;
    processes: fromProcesses.State;
    version: fromVersion.State;

    // router: fromRouter.RouterReducerState;
}

export const initialState: State = {
    core: fromCore.initialState,
    loading: fromLoading.initialState,
    auth: fromAuthorization.initialState,
    [userFeatureKey]: fromUser.initialState,
    lookups: fromLookups.initialState,
    reports: fromReports.initialState,
    user_role: fromUserRole.initialState,
    errors: fromErrors.initialState,
    expenses: fromExpenses.initialState,
    companySettings: fromCompanySetting.initialState, // how to use fromCompanySetting.companySettingsFeatureKey?
    oversignTime: fromOversign.initialState,
    processes: fromProcesses.initialState,
    version: fromVersion.initialState
    // , errors: fromShared.Errors.initialState
    // , expenses: fromExpenses.initialState
};

/**
 * Our state is composed of a map of action reducer functions.
 * These reducer functions are called with each dispatched action
 * and the current or initial state and return a new immutable state.
 */
export const reducers: ActionReducerMap<State> = {
    core: fromCore.reducer,
    loading: fromLoading.reducer,
    auth: fromAuthorization.reducer,
    [userFeatureKey]: fromUser.reducer,
    lookups: fromLookups.reducer,
    reports: fromReports.reducer,
    user_role: fromUserRole.reducer,
    errors: fromErrors.reducer,
    expenses: fromExpenses.reducer,
    companySettings: fromCompanySetting.reducer, // how to use fromCompanySetting.companySettingsFeatureKey?
    oversignTime: fromOversign.reducer,
    processes: fromProcesses.reducer,
    version: fromVersion.reducer
    // router: fromRouter.routerReducer,
};

// log all actions
export function logger(reducer: ActionReducer<State>): ActionReducer<State> {
    return function (state: State | undefined, action: Action): State {
        log.verbose({ type: 'redux', state, action });

        return reducer(state, action);
    };
}

//these are the keys that will be stored in local storage and session storage
const keys:string[] = [
    'core',
    'auth',
    userFeatureKey,
    fromQuickbooks.quickbooksFeatureKey,
    fromAuths.userAuthorizationsFeatureKey,
    'errors',
    'expenses',
    'lookups',
    'logoes',
    'reports',
    'user_role',
    'errors',
    'manageusers',
    fromCompanySetting.companySettingsFeatureKey,
    fromLookups.ScheduledDateReducer.scheduledDatesFeatureKey,
    fromOversign.oversignTimeFeatureKey,
    fromProcesses.processesFeatureKey,
    'entityCache',
    fromUserSetting.userSettingsFeatureKey,
    fromDataServices.Registration.entityName
];

// TODO: do we need to move user for lazy loading?
export function localStorageSyncReducer(
    reducer: ActionReducer<any>
): ActionReducer<any> {

    // localStorageSync({keys: [{todos: ['name', 'status'] }, ... ]})).
    return localStorageSync({
        keys: keys,
        rehydrate: true
    })(reducer);
}

export function sessionStorageSyncReducer(
    reducer: ActionReducer<any>
): ActionReducer<any> {
    return localStorageSync({
        keys: keys,
        rehydrate: true,
        storage: sessionStorage
    })(reducer);
}

export function clearState(reducer) {
    return function (state, action) {
        let result = state;
        // log.verbose({ type: 'root.reducer:clearState', action });
        if (
            action.type === fromActions.ActionTypes.Logout ||
            action.type === fromAuthorization.AuthActionTypes.Logout ||
            action.type === fromActions.ActionTypes.Reset
        ) {
            // log.verbose({ type: 'root.reducer:clearState:clearing', action });
            if (state.auth.rememberMe) {
                result = {
                    auth: {
                        username: state.auth.username,
                        pin: state.auth.pin,
                        rememberMe: state.auth.rememberMe,
                        status: LoginRequestStatus.Undefined,
                        token: undefined,
                        loggedIn: false
                    },
                    logoes: {
                        ...state.logoes
                    }
                };
            } else {
                // state = undefined;
                result = {
                    logoes: {
                        ...state.logoes
                    }
                };
            }
        }

        return reducer(result, action);
    };
}

// const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];
/**
 * By default, @ngrx/store uses combineReducers with the reducer map to compose
 * the root meta-reducer. To add more meta-reducers, provide an array of meta-reducers
 * that will be composed to form the root meta-reducer.
 */
export const metaReducers: MetaReducer<State>[] = !environment.production
    ? [
          clearState,
          localStorageSyncReducer,
          sessionStorageSyncReducer,
          storeFreeze
          //   logger
      ]
    : [clearState, localStorageSyncReducer, sessionStorageSyncReducer];
// export const metaReducers: MetaReducer<State>[] = [resetState];
